import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { LocalStorageKeys, SessionStorageKeys } from '@constants/enums';
import mixpanelSvc from './mixpanel/mixpanelSvc';

const proxyurl = import.meta.env.VITE_PROXY_URL || '';
const uri = import.meta.env.VITE_BACKEND_BASE_URL;
const cognitoUri = import.meta.env.VITE_COGNITO_BASE_URL;

interface ClientConfiguration extends AxiosRequestConfig {
  isAuthRedirect: boolean;
}

export interface ClientAxiosInstance {
  get<T = any>(url: string, configruation?: ClientConfiguration): Promise<T>;
  post<T = any>(
    url: string,
    data?: any,
    configruation?: ClientConfiguration
  ): Promise<T>;
  put<T = any>(
    url: string,
    data?: any,
    configruation?: ClientConfiguration
  ): Promise<T>;
  delete<T = any>(url: string, configruation?: ClientConfiguration): Promise<T>;
  patch<T = any>(
    url: string,
    data?: any,
    configruation?: ClientConfiguration
  ): Promise<T>;
}

export const client = axios.create({
  baseURL: `${proxyurl + uri}`,
});

export const clearAuthHeaders = (): void => {
  delete client.defaults.headers.common.Authorization;
};

const getCurrentAuthToken = (): string | null => {
  const legacyToken = localStorage.getItem(LocalStorageKeys.TOKEN);
  if (legacyToken) {
    return legacyToken;
  }

  const accessToken = sessionStorage.getItem(SessionStorageKeys.ACCESS_TOKEN);
  const idToken = sessionStorage.getItem(SessionStorageKeys.ID_TOKEN);

  return accessToken || idToken || null;
};

const handleRequestError = (
  error: AxiosError,
  { isAuthRedirect }: { isAuthRedirect: boolean }
) => {
  if (error.response) {
    const isAuthError = [401, 403].includes(error.response.status);
    if (isAuthError && isAuthRedirect) {
      localStorage.removeItem(LocalStorageKeys.TOKEN);
      mixpanelSvc.track('logout auth error', error.response);
      const onboardingData = localStorage.getItem(
        LocalStorageKeys.ONBOARDING_PROCESS_DATA
      );
      localStorage.clear();
      sessionStorage.clear();
      clearAuthHeaders();
      if (onboardingData) {
        localStorage.setItem(
          LocalStorageKeys.ONBOARDING_PROCESS_DATA,
          onboardingData
        );
      }
      window.location.reload();
    }
  }
  return Promise.reject(error);
};

// Add request interceptor to set auth header dynamically
client.interceptors.request.use(
  (config) => {
    const token = getCurrentAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      delete config.headers.Authorization;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

client.interceptors.response.use((response: AxiosResponse) => {
  if (response.data.token || response.data.token) {
    client.defaults.headers.common = {
      Authorization: `Bearer ${response.data.token}`,
    };
  }
  return response.data ? response.data.data ?? response.data : response;
});

const ignoredRoutes = [
  '2fa/setup',
  '2fa/verify',
  'check-password',
  '/api-token-auth/',
  '/payables',
  /\/enterprise\/(\d+)\/payments\/\?page=(\d+)&_v=(\d+)/,
];

const createAxiosClient = ({ isAuthRedirect }: { isAuthRedirect: boolean }) => {
  client.interceptors.response.use(
    (response: AxiosResponse) => response,
    (error: AxiosError) => {
      if (!ignoredRoutes.some((route) => error.config?.url?.match(route))) {
        return handleRequestError(error, { isAuthRedirect });
      }
      return Promise.reject(error);
    }
  );
  return client;
};

const getClient = (
  configuration = {
    isAuthRedirect: true,
  }
): ClientAxiosInstance =>
  configuration.isAuthRedirect
    ? createAxiosClient({ isAuthRedirect: true })
    : createAxiosClient({ isAuthRedirect: false });

export const cognitoClient = axios.create({
  baseURL: cognitoUri,
  headers: {
    'Content-Type': 'application/json',
  },
});

cognitoClient.interceptors.response.use(
  (response: AxiosResponse) => response.data,
  (error: AxiosError) => Promise.reject(error)
);

export default getClient;
